<template>
  <v-container fluid>
    <v-card>
      <v-card-title class="flex-wrap">
        Blogs
        <v-spacer />
        <v-btn text class="mr-2" :to="{ name: 'BlogCategories' }">
          Categories
        </v-btn>
        <v-btn color="primary" :to="{ name: 'BlogCreate' }">
          <v-icon left>mdi-plus</v-icon>New blog
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="search"
              label="Search title or excerpt"
              prepend-inner-icon="mdi-magnify"
              clearable
              outlined
              dense
              hide-details
              @input="onSearchInput"
            />
          </v-col>
          <v-col cols="6" md="3">
            <v-select
              v-model="categoryId"
              :items="categories"
              item-text="name"
              item-value="id"
              label="Category"
              clearable
              outlined
              dense
              hide-details
              @change="onFilterChange"
            />
          </v-col>
          <v-col cols="6" md="3">
            <v-select
              v-model="status"
              :items="statuses"
              label="Status"
              clearable
              outlined
              dense
              hide-details
              @change="onFilterChange"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
      >
        <template v-slot:item.title="{ item }">
          {{ item.title }}
          <v-chip
            v-if="Number(item.is_main) === 1"
            x-small
            dark
            color="orange"
            class="ml-2"
          >
            Main
          </v-chip>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip small dark :color="statusColor(item.status)">
            {{ item.status }}
          </v-chip>
        </template>

        <template v-slot:item.is_approved="{ item }">
          <v-chip
            small
            :dark="Number(item.is_approved) === 1"
            :color="Number(item.is_approved) === 1 ? 'teal' : 'grey lighten-2'"
          >
            {{ Number(item.is_approved) === 1 ? "Approved" : "Pending" }}
          </v-chip>
        </template>

        <template v-slot:item.published_at="{ item }">
          {{ item.published_at || "-" }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn icon small :to="{ name: 'BlogEdit', params: { id: item.id } }">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            v-if="Number(item.is_approved) !== 1"
            icon
            small
            color="teal"
            title="Approve"
            :disabled="saving"
            @click="setApproval(item, true)"
          >
            <v-icon small>mdi-check-circle-outline</v-icon>
          </v-btn>
          <v-btn
            v-else
            icon
            small
            color="orange"
            title="Remove approval"
            :disabled="saving"
            @click="askUnapprove(item)"
          >
            <v-icon small>mdi-close-circle-outline</v-icon>
          </v-btn>
          <v-btn icon small color="error" @click="askDelete(item)">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card>
        <v-card-title class="headline">Delete blog?</v-card-title>
        <v-card-text>
          "{{ toDelete && toDelete.title }}" will be permanently deleted.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="saving" @click="deleteDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" :loading="saving" @click="confirmDelete">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="unapproveDialog" max-width="420">
      <v-card>
        <v-card-title class="headline">Remove approval?</v-card-title>
        <v-card-text>
          "{{ toUnapprove && toUnapprove.title }}" will need approval again
          before it can be published.
          <span v-if="toUnapprove && toUnapprove.status === 'published'">
            It is live now, so it will be moved back to draft and taken off the
            public blog.
          </span>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="saving" @click="unapproveDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="orange"
            dark
            :loading="saving"
            @click="confirmUnapprove"
          >
            Remove approval
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";

export default {
  name: "Blogs",
  data() {
    return {
      options: { page: 1, itemsPerPage: 10 },
      search: "",
      categoryId: null,
      status: null,
      statuses: [
        { text: "Draft", value: "draft" },
        { text: "Published", value: "published" },
        { text: "Archived", value: "archived" },
      ],
      headers: [
        { text: "Title", value: "title", sortable: false },
        { text: "Category", value: "category_title", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "Approval", value: "is_approved", sortable: false },
        { text: "Published", value: "published_at", sortable: false },
        { text: "Actions", value: "actions", sortable: false, align: "end" },
      ],
      searchTimer: null,
      deleteDialog: false,
      toDelete: null,
      unapproveDialog: false,
      toUnapprove: null,
      snackbar: { show: false, text: "", color: "success" },
    };
  },
  computed: {
    ...mapState("blogs", [
      "items",
      "total",
      "categories",
      "loading",
      "saving",
      "error",
    ]),
  },
  watch: {
    // The data table emits its initial options on mount, which triggers the first load.
    options: {
      handler() {
        this.loadBlogs();
      },
      deep: true,
    },
  },
  created() {
    this.getCategories();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    ...mapActions("blogs", [
      "getBlogs",
      "getCategories",
      "deleteBlog",
      "approveBlog",
      "unapproveBlog",
    ]),

    async setApproval(item, approve) {
      const ok = approve
        ? await this.approveBlog(item.id)
        : await this.unapproveBlog(item.id);

      if (ok) {
        this.snackbar = {
          show: true,
          text: approve ? "Blog approved." : "Approval removed.",
          color: "success",
        };
        this.loadBlogs();
      }
    },

    askUnapprove(item) {
      this.toUnapprove = item;
      this.unapproveDialog = true;
    },

    async confirmUnapprove() {
      await this.setApproval(this.toUnapprove, false);
      this.unapproveDialog = false;
    },

    statusColor(status) {
      return (
        { published: "green", draft: "orange", archived: "grey" }[status] ||
        "grey"
      );
    },

    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.onFilterChange, 400);
    },

    onFilterChange() {
      if (this.options.page !== 1) {
        this.options = { ...this.options, page: 1 }; // the watcher loads
      } else {
        this.loadBlogs();
      }
    },

    async loadBlogs() {
      const params = {
        page: this.options.page,
        per_page: this.options.itemsPerPage,
      };
      if (this.search) params.search = this.search;
      if (this.categoryId) params.category_id = this.categoryId;
      if (this.status) params.status = this.status;

      await this.getBlogs(new URLSearchParams(params).toString());
    },

    askDelete(item) {
      this.toDelete = item;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      await this.deleteBlog(this.toDelete.id);
      this.deleteDialog = false;

      // error === null means the request succeeded
      if (this.error === null) {
        this.snackbar = { show: true, text: "Blog deleted.", color: "success" };
        this.loadBlogs();
      }
    },
  },
};
</script>
